import {
  ApartmentOutlined,
  ExclamationCircleFilled,
  PartitionOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  } from "@ant-design/icons";
import {
  Button,
  Card,
  Empty,
  Flex,
  Input,
  message,
  Modal,
  Space,
  Table,
  Typography,
} from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { baseUrl } from "../../../components/common/baseUrl";
import { useNotify } from "../../../components/Notification";
import PageHeader from "../../../components/PageLayout/PageHeader";
import FilterBar from "../../../components/PageLayout/FilterBar";
import MetricCard from "../../../components/PageLayout/MetricCard";
import { brand, semantic } from "../../../theme/tokens";
import { settings } from "../../../utils/config";
import ModalAddPermission from "./components/ModalAddPermission";
import ModelEditPermission from "./components/ModelEditPermission";
import RowActions from "../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import StatusTag from "../../../components/cms/StatusTag";

const { confirm } = Modal;
const { Text } = Typography;

const ListPermission = () => {
  const notify = useNotify();
  const hasAccessToken = settings?.getCookie("access_token");

  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [permission, setPermission] = useState();
  const [valueSearch, setValueSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [listPermission, setListPermission] = useState([]);

  const permissionRows = Array.isArray(listPermission) ? listPermission : [];
  const stats = useMemo(() => {
    const parent = permissionRows.filter((item) => item.level === 0).length;
    return {
      total: permissionRows.length,
      parent,
      child: Math.max(permissionRows.length - parent, 0),
    };
  }, [permissionRows]);

  const buildVisibleList = (tree, level = 0) => {
    if (!Array.isArray(tree)) return [];

    let result = [];
    for (let item of tree) {
      const { children, ...rest } = item;
      result.push({ ...rest, level });
      if (Array.isArray(children) && children.length > 0) {
        result = result.concat(buildVisibleList(children, level + 1));
      }
    }
    return result;
  };

  const handleApiError = (error, defaultMessage) => {
    if (error.response) {
      const status = error.response.status;
      const errorMessage = error.response.data?.message || defaultMessage;

      if (status === 401) {
        message.error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!");
      } else if (status === 403) {
        message.error("Bạn không có quyền truy cập chức năng này!");
      } else if (status === 404) {
        message.error("Không tìm thấy dữ liệu!");
      } else {
        message.error(`Lỗi ${status}: ${errorMessage}`);
      }
    } else if (error.request) {
      message.error("Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng!");
    } else {
      message.error(defaultMessage);
    }
  };

  const fetchActions = async (search) => {
    try {
      setIsLoading(true);
      const keyword = search?.trim() || "";
      let url = `${baseUrl}/api/v1/security/permissions/get-list-all-permission`;

      if (keyword) {
        url += `?search=${encodeURIComponent(keyword)}`;
      }

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hasAccessToken}`,
        },
      });

      if (response.data.success && response.data.data) {
        setListPermission(buildVisibleList(response.data.data));
        setAppliedSearch(keyword);
      }
    } catch (error) {
      handleApiError(error, "Không thể tải danh sách quyền");
    } finally {
      setIsLoading(false);
    }
  };

  const showDeleteConfirm = (record) => {
    confirm({
      title: `Xóa permission "${record.name}"?`,
      content: "Permission bị xóa có thể ảnh hưởng tới menu và tài khoản đang được phân quyền.",
      icon: <ExclamationCircleFilled />,
      okText: "Xóa permission",
      okType: "danger",
      cancelText: "Hủy",
      onOk: async () => {
        try {
          const url = `${baseUrl}/api/v1/security/permissions/${record.id}`;
          await axios.delete(url, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${hasAccessToken}`,
            },
          });

          notify.success({
            message: "Thành công",
            description: "Xóa permission thành công!",
          });

          await fetchActions(appliedSearch);
        } catch (error) {
          handleApiError(error, "Xóa permission thất bại");
        }
      },
      onCancel() {},
    });
  };

  const handleSubmitPermission = async (data) => {
    const request = {
      name: data.name,
      label: data.label,
      parent_id: data.parent_id || null,
    };

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${baseUrl}/api/v1/security/permissions`,
        request,
        { headers: { Authorization: `Bearer ${hasAccessToken}` } }
      );

      if (response.data) {
        notify.success({
          message: "Thành công",
          description: "Tạo permission thành công!",
        });
        fetchActions(appliedSearch);
      }
    } catch (error) {
      const errors = error.response?.data?.errors || {};
      const errorMessages = Object.entries(errors)
        .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
        .join(" | ");
      notify.error({
        message: "Thất bại",
        description: errorMessages || "Không thể tạo permission",
      });
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  };

  const handleEditPermission = async (data) => {
    try {
      setIsLoading(true);
      const response = await axios.put(
        `${baseUrl}/api/v1/security/permissions/${permission?.id}`,
        data,
        { headers: { Authorization: `Bearer ${hasAccessToken}` } }
      );

      if (response) {
        notify.success({
          message: "Thành công",
          description: "Cập nhật permission thành công!",
        });
        fetchActions(appliedSearch);
      }
    } catch (error) {
      const errors = error.response?.data?.errors || {};
      const errorMessages = Object.entries(errors)
        .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
        .join(" | ");
      notify.error({
        message: "Thất bại",
        description: errorMessages || "Không thể sửa permission",
      });
    } finally {
      setIsLoading(false);
      setOpenEdit(false);
    }
  };

  const handleSearch = () => {
    fetchActions(valueSearch);
  };

  const handleResetSearch = () => {
    setValueSearch("");
    setAppliedSearch("");
    fetchActions("");
  };

  const columns = [
    {
      title: "Permission",
      dataIndex: "label",
      key: "label",
      fixed: "left",
      width: 360,
      render: (_, record) => {
        const isParent = record.level === 0;

        return (
          <Flex
            align="center"
            gap={10}
            style={{ paddingLeft: 24 * (record.level ?? 0) }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                backgroundColor: isParent ? "#eff6ff" : "#f8fafc",
                color: isParent ? "#1d4ed8" : "#64748b",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
              }}
            >
              {isParent ? <SafetyCertificateOutlined /> : "└"}
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  color: isParent ? "#0f172a" : "#334155",
                  fontWeight: isParent ? 700 : 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={record.label}
              >
                {record.label || "Chưa có mô tả"}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {isParent ? "Nhóm quyền" : "Quyền con"}
              </Text>
            </div>
          </Flex>
        );
      },
    },
    {
      title: "Mã permission",
      dataIndex: "name",
      key: "name",
      width: 320,
      render: (name) =>
        name ? <StatusTag>{name}</StatusTag> : <Text type="secondary">Không có</Text>,
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      align: "right",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <RowActions
          items={[
            {
              key: "edit",
              icon: ACTION_ICON.edit,
              label: "Sửa permission",
              onClick: () => {
                setOpenEdit(true);
                setPermission(record);
              },
            },
            {
              key: "delete",
              icon: ACTION_ICON.delete,
              label: "Xóa permission",
              danger: true,
              divider: true,
              // Màn này đã có hộp thoại xác nhận riêng — giữ nguyên.
              onClick: () => showDeleteConfirm(record),
            },
          ]}
        />
      ),
    },
  ];

  useEffect(() => {
    if (valueSearch.trim() === "") {
      fetchActions("");
    }
  }, [valueSearch]);

  return (
    <div>
      <PageHeader
        title="Quản lý quyền"
        subtitle="Quản lý nhóm quyền và mã permission dùng cho menu, tài khoản và thao tác trong CMS."
        breadcrumb={[
          { title: "Phân quyền" },
          { title: <NavLink to="/permission">Quản lý quyền</NavLink> },
        ]}
        extra={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => fetchActions(appliedSearch)} loading={isLoading}>
              Tải lại
            </Button>
            <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => setOpen(true)}>
              Thêm permission
            </Button>
          </Space>
        }
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, padding: "16px 16px 0" }}>
        <div style={{ flex: "1 1 200px", minWidth: 200 }}>
          <MetricCard icon={<SafetyCertificateOutlined />} color={brand.accent} label="Tổng quyền" value={stats.total} />
        </div>
        <div style={{ flex: "1 1 200px", minWidth: 200 }}>
          <MetricCard icon={<ApartmentOutlined />} color={semantic.info} label="Nhóm quyền" value={stats.parent} />
        </div>
        <div style={{ flex: "1 1 200px", minWidth: 200 }}>
          <MetricCard icon={<PartitionOutlined />} color={semantic.success} label="Quyền con" value={stats.child} />
        </div>
      </div>

      <FilterBar style={{ marginTop: 16, borderTop: "1px solid #F1F5F9" }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Tìm theo mã hoặc mô tả permission..."
          value={valueSearch}
          onChange={(event) => setValueSearch(event.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 360, maxWidth: "100%" }}
        />
        <Button type="primary" onClick={handleSearch} loading={isLoading}>
          Tìm
        </Button>
        {appliedSearch && <Button onClick={handleResetSearch}>Xóa lọc</Button>}
      </FilterBar>

      <Card
        style={{ margin: 16 }}
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>Danh sách quyền</span>
          </Space>
        }
        extra={
          appliedSearch ? (
            <Text type="secondary">
              Đang lọc “{appliedSearch}” · {permissionRows.length} quyền
            </Text>
          ) : null
        }
      >
        <Table
          className="custom-table"
          columns={columns}
          dataSource={permissionRows}
          rowKey="id"
          pagination={false}
          loading={isLoading}
          size="middle"
          scroll={{ y: 55 * 10, x: "max-content" }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={appliedSearch ? "Không tìm thấy permission phù hợp" : "Chưa có permission"}
              />
            ),
          }}
        />
      </Card>

      {open && (
        <ModalAddPermission
          isOpen={open}
          onOk={(data) => handleSubmitPermission(data)}
          onCancel={() => setOpen(false)}
        />
      )}
      {openEdit && (
        <ModelEditPermission
          isOpen={openEdit}
          onOk={(data) => handleEditPermission(data)}
          onCancel={() => setOpenEdit(false)}
          permission={permission}
        />
      )}
    </div>
  );
};

export default ListPermission;